import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  DMAutomation,
  DMAutomationPlatform,
  DMAutomationState,
  InstagramTrigger,
  IntegrationConnectionStatus,
  PlanTypeV4,
} from "@markit/common.types";
import { Icon } from "@iconify/react";
import SearchBoxContainer from "../../Containers/SearchBoxContainer";
import { TabPanel } from "../../FullEventSubComponents/TabPanel";
import EmptyStateButton from "../../Buttons/EmptyStateButton";
import { EmptyStateFlatlist } from "../../EmptyStates/EmptyStateFlatlist";
import StandardListContainer from "../../Containers/StandardListContainer";
import DropdownFilter from "../../DropdownsAndTabs/DropdownFilter";
import { DropdownMenuItem } from "../../DropdownMenu";
import DropdownMultiSelectMenu from "../../DropdownsAndTabs/DropdownMultiSelectMenu";
import DropdownArrowIcon from "../../DropdownsAndTabs/DropdownArrowIcon";
import SocialProfilePic from "../../DisplayItem/AutoDM/SocialProfilePic";
import { getAccountSlice } from "../../../redux/slices/accountSlice";
import { useSelector } from "react-redux";
import { AutoDMTabs } from "../../../screens/Profile/CreatorPanels/GrowPanels/AutoDMPanel";
import { useLoadDMAutomationItems } from "../../../hooks/useLoadDMAutomationItems";
import { AutoDMItemPreview } from "../../DisplayItem/AutoDM/AutoDMItemPreview";
import { HorizontalDivider } from "../../Dividers/HorizontalDivider";
import { facebookPageAsProfilePicAccount } from "../../../utils/facebookApiUtils";
import { hasSocialAccountConnection } from "../../../utils/socialAccountUtils";
import FlatlistLoadMore from "../../FlatlistLoadMore";
import { hasSubscription } from "@markit/common.utils";
import LabelButton from "../../Buttons/LabelButton";
import EmptyStateButtonSubscription from "../../Buttons/EmptyState/EmptyStateButtonSubscription";
import { addAlpha, Colors } from "../../../utils/colors";
import { SocialPlatformIconItem } from "../../DisplayItem/AutoDM/SocialPlatformIconItem";

// One entry per connected account, across both platforms, for the unified accounts
// filter dropdown - a plain string id array can't tell you which platform an id
// belongs to, but the filter UI itself needs to show each account's platform icon,
// so this pairs the two.
type AccountFilterEntry = {
  id: string;
  platform: DMAutomationPlatform;
  label: string;
  profilePicUrl: string;
};

// Trigger types the automations list can be filtered by
const TRIGGER_TYPE_FILTER_ITEMS = [
  { id: InstagramTrigger.COMMENT_POST_REEL, label: "Post or Reel Comments" },
  { id: InstagramTrigger.REPLY_STORY, label: "Story Replies" },
  { id: InstagramTrigger.REPLY_DM, label: "DM Keywords" },
];

enum AutoDMAutomationFilterState {
  ACTIVE = "Active",
  INACTIVE = "Inactive",
}

type AutoDMAutomationsPanelProps = {
  tabValue: number;
  setTabValue: (tabValue: number) => void;
  setSelectTriggerModal: (selectTriggerModal: boolean) => void;
};

const AutoDMAutomationsPanel = (props: AutoDMAutomationsPanelProps) => {
  const { tabValue, setTabValue, setSelectTriggerModal } = props;
  const { accountData, instagramAccounts, facebookPages } =
    useSelector(getAccountSlice);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [filterState, setFilterState] = useState<AutoDMAutomationFilterState>(
    AutoDMAutomationFilterState.ACTIVE
  );
  const [selectedAccountIds, setSelectedAccountIds] = useState<string[]>([]);
  const [accountFilterOpen, setAccountFilterOpen] = useState(false);
  // Empty = all trigger types (same convention as the accounts filter)
  const [selectedTriggerTypes, setSelectedTriggerTypes] = useState<string[]>(
    []
  );
  const [triggerFilterOpen, setTriggerFilterOpen] = useState(false);

  const styles = {
    dropdownContainer: {
      gap: 7,
      borderRadius: 8,
      paddingBlock: 12,
      paddingInline: 14,
      backgroundColor: Colors.WHITE1,
      border: `0.5px solid ${Colors.GRAY2}`,
    },
  };

  const {
    fetchedDMAutomations,
    fetchedTriggers,
    fetchedContents,
    campaignMessages,
    submissionCounts,
    isLoading: isLoadingDMAutomations,
    isFinished: isFinishedDMAutomations,
    loadDMAutomations,
    loadMoreDMAutomations,
    resetAndReloadDMAutomations,
  } = useLoadDMAutomationItems({
    userId: accountData.uid,
    windowSize: 15,
    selectedAccountIds,
    trustedSender: accountData.trustedSender,
  });

  const hasAnyConnection = useMemo(
    () =>
      hasSocialAccountConnection(instagramAccounts) ||
      hasSocialAccountConnection(facebookPages),
    [instagramAccounts, facebookPages]
  );

  // Load automations if connected to Instagram or Facebook, nothing loaded yet, not
  // loading, and not finished.
  useEffect(() => {
    if (
      hasAnyConnection &&
      fetchedDMAutomations.length === 0 &&
      !isLoadingDMAutomations &&
      !isFinishedDMAutomations
    ) {
      loadDMAutomations();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    accountData,
    hasAnyConnection,
    fetchedDMAutomations.length,
    isLoadingDMAutomations,
    isFinishedDMAutomations,
  ]);

  // Account filter changes the query itself (not a client-side re-filter), so it needs a fresh fetch, not an incremental loadMore.
  const previousSelectedAccountIdsRef = useRef(selectedAccountIds);
  useEffect(() => {
    const previous = previousSelectedAccountIdsRef.current;
    previousSelectedAccountIdsRef.current = selectedAccountIds;
    const changed =
      previous.length !== selectedAccountIds.length ||
      !previous.every((id) => selectedAccountIds.includes(id));
    if (!changed) {
      return;
    }
    if (hasAnyConnection) {
      resetAndReloadDMAutomations();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAccountIds]);

  const hasSub = useMemo(
    () => hasSubscription(accountData) || accountData.isAdmin,
    [accountData]
  );

  const allConnectedAccounts: AccountFilterEntry[] = useMemo(
    () => [
      ...instagramAccounts.map((account) => ({
        id: account.id,
        platform: DMAutomationPlatform.INSTAGRAM,
        label: account.username ? `@${account.username}` : account.id,
        profilePicUrl: account.profilePicUrl,
      })),
      ...facebookPages.map((page) => ({
        id: page.id,
        platform: DMAutomationPlatform.FACEBOOK,
        label: page.pageName || page.id,
        profilePicUrl: page.profilePicUrl,
      })),
    ],
    [instagramAccounts, facebookPages]
  );

  const errorAccountFound = useMemo(
    () =>
      instagramAccounts.some(
        (account) => account.status === IntegrationConnectionStatus.ERROR
      ) ||
      facebookPages.some(
        (page) => page.status === IntegrationConnectionStatus.ERROR
      ),
    [instagramAccounts, facebookPages]
  );

  const allAccountsPaused = useMemo(
    () =>
      allConnectedAccounts.length > 0 &&
      instagramAccounts.every(
        (account) => account.status === IntegrationConnectionStatus.PAUSED
      ) &&
      facebookPages.every(
        (page) => page.status === IntegrationConnectionStatus.PAUSED
      ),
    [allConnectedAccounts.length, instagramAccounts, facebookPages]
  );

  const handleSearch = (text: string) => {
    setSearchTerm(text.toLowerCase());
  };

  const visibleAutomations = useMemo(() => {
    return fetchedDMAutomations.filter((dm) => {
      if (filterState === AutoDMAutomationFilterState.ACTIVE) {
        return (
          dm.state === DMAutomationState.ACTIVE ||
          dm.state === DMAutomationState.DRAFT
        );
      }
      return dm.state === DMAutomationState.INACTIVE;
    });
  }, [fetchedDMAutomations, filterState]);

  // Keeps only automations with at least one trigger of a selected type
  const triggerFilteredAutomations = useMemo(() => {
    if (selectedTriggerTypes.length === 0) {
      return visibleAutomations;
    }
    return visibleAutomations.filter((dm) =>
      fetchedTriggers.some(
        (trigger) =>
          trigger.dmAutomationId === dm.id &&
          selectedTriggerTypes.includes(trigger.type)
      )
    );
  }, [visibleAutomations, fetchedTriggers, selectedTriggerTypes]);

  const filteredAutomations = useMemo(() => {
    if (!searchTerm.trim()) {
      return triggerFilteredAutomations;
    }
    return triggerFilteredAutomations.filter((dm) =>
      dm.title.toLowerCase().includes(searchTerm)
    );
  }, [triggerFilteredAutomations, searchTerm]);

  const renderDMAutomationItem = useCallback(
    (item: DMAutomation) => {
      const itemTriggers = fetchedTriggers.filter(
        (trigger) => trigger.dmAutomationId === item.id
      );
      const firstTrigger = itemTriggers[0];
      const triggerCount = itemTriggers.length;
      const submissionCount = submissionCounts[item.id];
      const triggeredTotal = itemTriggers.reduce(
        (sum, trigger) => sum + (trigger.numTriggered ?? 0),
        0
      );
      const dmMessageContent = fetchedContents.find(
        (content) => content.dmAutomationId === item.id
      );
      const campaignMessage = campaignMessages[item.id];
      return (
        <div key={item.id}>
          <AutoDMItemPreview
            dmAutomation={item}
            firstTrigger={firstTrigger}
            triggerCount={triggerCount}
            submissionCount={submissionCount}
            triggeredTotal={triggeredTotal}
            dmMessageContent={dmMessageContent}
            campaignMessage={campaignMessage}
          />
          <HorizontalDivider altMargin={14} />
        </div>
      );
    },
    [fetchedTriggers, fetchedContents, campaignMessages, submissionCounts]
  );

  const renderEmptyView = useMemo(() => {
    return (
      <EmptyStateFlatlist
        searchTerm={searchTerm}
        isLoading={isLoadingDMAutomations}
        containerStyles={{ paddingTop: 120 }}
        nonSearchEmptyView={
          !hasAnyConnection ? (
            !hasSub ? (
              <EmptyStateButtonSubscription
                planType={PlanTypeV4.ESSENTIAL}
                iconName={"fa7-solid:plug"}
                description="Create automations to convert your audience from social media to Markit"
                btnText="Get Essential Plan"
              />
            ) : (
              <EmptyStateButton
                title="Connect to Start Automations"
                description="Create automations to convert your audience from social media to Markit"
                icon={<Icon icon={"fa7-solid:plug"} height={63} />}
                iconBox={80}
                containerStyles={{ paddingTop: 120 }}
                onPress={() => setTabValue(AutoDMTabs.CONNECTION)}
                btnText="Connect"
              />
            )
          ) : (
            <EmptyStateButton
              title={"No Automations Found"}
              description={
                "There were no automations found within this section."
              }
              icon={<Icon icon={"fa-solid:network-wired"} height={50} />}
              iconBox={80}
              containerStyles={{ paddingTop: 120 }}
              onPress={() => setSelectTriggerModal(true)}
              btnText="New Automation"
            />
          )
        }
      />
    );
  }, [
    hasAnyConnection,
    hasSub,
    isLoadingDMAutomations,
    searchTerm,
    setSelectTriggerModal,
    setTabValue,
  ]);

  const dropdownItems: DropdownMenuItem[] = useMemo(
    () => [
      {
        title: AutoDMAutomationFilterState.ACTIVE,
        icon:
          filterState === AutoDMAutomationFilterState.ACTIVE
            ? "ion:checkmark"
            : undefined,
        key: "active_dm_automations",
        onPress: () => setFilterState(AutoDMAutomationFilterState.ACTIVE),
      },
      {
        title: AutoDMAutomationFilterState.INACTIVE,
        icon:
          filterState === AutoDMAutomationFilterState.INACTIVE
            ? "ion:checkmark"
            : undefined,
        key: "inactive_dm_automations",
        onPress: () => setFilterState(AutoDMAutomationFilterState.INACTIVE),
      },
    ],
    [filterState]
  );

  return (
    <TabPanel value={tabValue} index={0}>
      <StandardListContainer
        searchComp={
          <div className="AlignedRowSpaced" style={{ width: "100%" }}>
            <div className="AlignedRow" style={{ gap: 14 }}>
              <SearchBoxContainer
                placeholder={"Search"}
                onChange={(e) => handleSearch(e.target.value)}
                containerStyles={{ marginTop: 0, width: 394 }}
              />
              <DropdownFilter items={dropdownItems} filterState={filterState} />
              <DropdownMultiSelectMenu
                items={TRIGGER_TYPE_FILTER_ITEMS}
                getId={(item) => item.id}
                renderItem={(item) => (
                  <span className="bodyMedium OneLineText">{item.label}</span>
                )}
                selectedIds={selectedTriggerTypes}
                setSelectedIds={setSelectedTriggerTypes}
                isOpen={triggerFilterOpen}
                onOpenChange={setTriggerFilterOpen}
                allLabel="Any Trigger"
              >
                <div
                  className="AlignedRowSelect"
                  style={styles.dropdownContainer}
                >
                  <span className="bodyMedium">
                    {selectedTriggerTypes.length === 0 ||
                    selectedTriggerTypes.length ===
                      TRIGGER_TYPE_FILTER_ITEMS.length
                      ? "Any Trigger"
                      : selectedTriggerTypes.length === 1
                        ? (TRIGGER_TYPE_FILTER_ITEMS.find(
                            (item) => item.id === selectedTriggerTypes[0]
                          )?.label ?? "1 Trigger Selected")
                        : `${selectedTriggerTypes.length} Triggers Selected`}
                  </span>
                  <DropdownArrowIcon isOpen={triggerFilterOpen} height={14} />
                </div>
              </DropdownMultiSelectMenu>
              {allConnectedAccounts.length > 1 ? (
                <DropdownMultiSelectMenu
                  items={allConnectedAccounts}
                  getId={(entry) => entry.id}
                  renderItem={(entry) => (
                    <div
                      className="AlignedRow"
                      style={{ gap: 10, minWidth: 0 }}
                    >
                      <SocialProfilePic
                        account={
                          entry.platform === DMAutomationPlatform.FACEBOOK
                            ? facebookPageAsProfilePicAccount(
                                facebookPages.find((p) => p.id === entry.id)!
                              )
                            : instagramAccounts.find((a) => a.id === entry.id)
                        }
                        size={24}
                      />
                      <SocialPlatformIconItem
                        platform={entry.platform}
                        size={14}
                        noBorder
                      />
                      <span className="bodyMedium OneLineText">
                        {entry.label}
                      </span>
                    </div>
                  )}
                  selectedIds={selectedAccountIds}
                  setSelectedIds={setSelectedAccountIds}
                  isOpen={accountFilterOpen}
                  onOpenChange={setAccountFilterOpen}
                  disabled={allConnectedAccounts.length <= 1}
                  allLabel="All Accounts"
                >
                  <div
                    className="AlignedRowSelect"
                    style={styles.dropdownContainer}
                  >
                    <div className="AlignedRow">
                      {(selectedAccountIds.length > 0
                        ? allConnectedAccounts.filter((entry) =>
                            selectedAccountIds.includes(entry.id)
                          )
                        : allConnectedAccounts
                      ).map((entry, index) => (
                        <div
                          key={entry.id}
                          style={{
                            marginLeft: index === 0 ? 0 : -10,
                            flexShrink: 0,
                          }}
                        >
                          <SocialProfilePic
                            account={
                              entry.platform === DMAutomationPlatform.FACEBOOK
                                ? facebookPageAsProfilePicAccount(
                                    facebookPages.find(
                                      (p) => p.id === entry.id
                                    )!
                                  )
                                : instagramAccounts.find(
                                    (a) => a.id === entry.id
                                  )
                            }
                            size={20}
                            borderStyle={`2px solid ${Colors.WHITE}`}
                          />
                        </div>
                      ))}
                    </div>
                    <span className="bodyMedium">
                      {selectedAccountIds.length === 0
                        ? "All Accounts"
                        : `${selectedAccountIds.length} of ${allConnectedAccounts.length} Accounts`}
                    </span>
                    {allConnectedAccounts.length > 1 ? (
                      <DropdownArrowIcon
                        isOpen={accountFilterOpen}
                        height={14}
                      />
                    ) : null}
                  </div>
                </DropdownMultiSelectMenu>
              ) : null}
            </div>
            {errorAccountFound ? (
              <LabelButton
                color={Colors.RED3}
                backgroundColor={addAlpha(Colors.RED3, 0.1)}
                text="Account Reconnect Needed"
                borderColor={Colors.RED3}
                onPress={() => setTabValue(AutoDMTabs.CONNECTION)}
              />
            ) : allAccountsPaused ? (
              <LabelButton
                color={Colors.ORANGE1}
                backgroundColor={addAlpha(Colors.ORANGE1, 0.1)}
                text="All Automations Paused"
                borderColor={Colors.ORANGE1}
              />
            ) : null}
          </div>
        }
        listComp={
          <div className="ColumnNormal" style={{ flex: 1 }}>
            <FlatlistLoadMore
              fullList={filteredAutomations}
              currentList={filteredAutomations}
              renderItem={(item) => renderDMAutomationItem(item)}
              renderWhenEmpty={renderEmptyView}
              isLoadingMore={isLoadingDMAutomations}
              showLoadMore={!isFinishedDMAutomations}
              loadMoreOnPress={loadMoreDMAutomations}
            />
          </div>
        }
        isLoading={isLoadingDMAutomations}
      />
    </TabPanel>
  );
};

export default AutoDMAutomationsPanel;
